import dayjs from 'dayjs';
import 'dayjs/locale/fr';

import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Header,
  Input,
  type Job,
  Loader,
  Segment,
  TraitementLong,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';
import { ModalConfirm } from '@/components/Autre';
import { MODES_REGLEMENTS } from '@/constants';
import { ArretePeriodiquePointageContext } from '@/contexts';
import { useCreateArreteAnnuel, useReglementPointage } from '@/hooks';
import {
  getArretePrevisionnelPeriodique,
  postArrete,
  postArreteEditionsPeriodique,
} from '@/routes';
import { type ArreteType, EDITIONS_PERIODIQUES_OPTIONS, type EditionPeriodiqueType } from '@/types';
import { estDansFenetreArreteAnnuel } from '@/utils';
import { useRegieStore } from '@/zustand';

dayjs.locale('fr');

type PrevisionnelType = Partial<ArreteType> & {
  annee: number;
  rang: number;
  dateDebutPeriode: string;
  dateFinPeriode: string;
};

type ArretePeriodiqueEditionsProps = {
  onGoToListe: () => void;
};

const DROPDOWN_OPTIONS = [{ key: 'tous', value: 'tous', text: 'Tous les régisseurs' }];

const ArretePeriodiqueEditions = (props: ArretePeriodiqueEditionsProps) => {
  const { onGoToListe } = props;

  const [previsionnel, setPrevisionnel] = React.useState<PrevisionnelType | null>(null);
  const [loadingPrevisionnel, setLoadingPrevisionnel] = React.useState<boolean>(true);
  const [selectedEditions, setSelectedEditions] = React.useState<Set<EditionPeriodiqueType>>(
    () => new Set(),
  );
  const [hasClickedEdit, setHasClickedEdit] = React.useState<boolean>(false);
  const [editionsInFlight, setEditionsInFlight] = React.useState<boolean>(false);
  const [job, setJob] = React.useState<number | null>(null);
  const [confirmationOpen, setConfirmationOpen] = React.useState<boolean>(false);
  const [arretInFlight, setArretInFlight] = React.useState<boolean>(false);
  const [proposerAnnuel, setProposerAnnuel] = React.useState<boolean>(false);

  const updateRegie = useRegieStore((state) => state.updateRegie);
  const refreshRegie = useRegieStore((state) => state.refreshRegie);
  const arreteAnnuelRequis = useRegieStore((state) => state.regie?.arreteAnnuelRequis);
  const arreteAnnuelCas = useRegieStore((state) => state.regie?.arreteAnnuelCas);
  const arreteAnnuelAnnee = useRegieStore((state) => state.regie?.arreteAnnuelAnnee);
  const arreteConfig = useRegieStore((state) => state.regie?.arreteConfig);
  // Procédure d'arrêté annuel de rattrapage (Cas 1) : on antidate le périodique au 31/12 N-1
  // et on enchaîne automatiquement sur l'annuel.
  const isProcedureAnnuelle = Boolean(arreteAnnuelRequis) && arreteAnnuelCas === 1;
  const { createArreteAnnuel, creating: creatingAnnuel } = useCreateArreteAnnuel();

  // Sélection de période portée par PointageEtEditionsWrapper. En mode « libre », la date de
  // fin choisie borne le prévisionnel (fetch) et se propage aux éditions et à la finalisation.
  const { periodeMode, dateFin } = React.useContext(ArretePeriodiquePointageContext);
  const dateFinParam =
    periodeMode === 'libre' && dateFin ? dateFin.format('YYYY-MM-DD') : undefined;

  // 12 contextes fournis par PointageEtEditionsWrapper.
  const contextCheque = useReglementPointage(MODES_REGLEMENTS.CHEQUE);
  const contextNumeraire = useReglementPointage(MODES_REGLEMENTS.NUMERAIRE);
  const contextCESU = useReglementPointage(MODES_REGLEMENTS.CESU);
  const contextChequeVac = useReglementPointage(MODES_REGLEMENTS.CHEQUE_VAC);
  const contextCarteBancaire = useReglementPointage(MODES_REGLEMENTS.CARTE_BANCAIRE);
  const contextVirement = useReglementPointage(MODES_REGLEMENTS.VIREMENT);
  const contextPrelevement = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT);
  const contextCarteBancaireInternet = useReglementPointage(
    MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET,
  );
  const contextPrelevementInternet = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT_INTERNET);
  const contextTicket = useReglementPointage(MODES_REGLEMENTS.TICKET);
  const contextBonCCAS = useReglementPointage(MODES_REGLEMENTS.BON_CCAS);
  const contextBonCAF = useReglementPointage(MODES_REGLEMENTS.BON_CAF);

  // Chargement du prévisionnel au montage. Même logique de fallback { data } | brut
  // que ArretePeriodiqueListe.
  React.useEffect(() => {
    let cancelled = false;
    setLoadingPrevisionnel(true);

    (async () => {
      try {
        const response: any = await request.get(
          getArretePrevisionnelPeriodique(isProcedureAnnuelle, dateFinParam),
        );
        if (cancelled) return;
        const candidate = response && 'data' in response ? response.data : response;
        const isValid =
          candidate && typeof candidate === 'object' && 'annee' in candidate && 'rang' in candidate;
        if (!isValid) {
          toast.error('Aucun prévisionnel périodique disponible.');
          onGoToListe();
          return;
        }
        setPrevisionnel(candidate as PrevisionnelType);
      } catch {
        if (!cancelled) {
          toast.error('Erreur lors du chargement du prévisionnel.');
          onGoToListe();
        }
      } finally {
        if (!cancelled) setLoadingPrevisionnel(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onGoToListe, isProcedureAnnuelle, dateFinParam]);

  // Date de fin appliquée aux éditions et à la finalisation. En mode « libre », c'est la date
  // choisie ; sinon la fin de période du prévisionnel (déjà bornée par le fetch).
  const dateFinEffective = React.useMemo(
    () => (periodeMode === 'libre' && dateFin ? dateFin : dayjs(previsionnel?.dateFinPeriode)),
    [periodeMode, dateFin, previsionnel],
  );

  const hasActivityToday = React.useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const all = [
      contextCheque.reglements,
      contextNumeraire.reglements,
      contextCESU.reglements,
      contextChequeVac.reglements,
      contextCarteBancaire.reglements,
      contextVirement.reglements,
      contextPrelevement.reglements,
      contextCarteBancaireInternet.reglements,
      contextPrelevementInternet.reglements,
      contextTicket.reglements,
      contextBonCCAS.reglements,
      contextBonCAF.reglements,
    ];
    return all.some((list) =>
      (list ?? []).some(
        (r) =>
          dayjs(r.dateCreationRecu).format('YYYY-MM-DD') === today ||
          (!!r.dateAnnulationRecu && dayjs(r.dateAnnulationRecu).format('YYYY-MM-DD') === today),
      ),
    );
  }, [
    contextCheque.reglements,
    contextNumeraire.reglements,
    contextCESU.reglements,
    contextChequeVac.reglements,
    contextCarteBancaire.reglements,
    contextVirement.reglements,
    contextPrelevement.reglements,
    contextCarteBancaireInternet.reglements,
    contextPrelevementInternet.reglements,
    contextTicket.reglements,
    contextBonCCAS.reglements,
    contextBonCAF.reglements,
  ]);

  const handleParentToggle = React.useCallback(() => {
    setSelectedEditions((prev) => {
      if (prev.size === EDITIONS_PERIODIQUES_OPTIONS.length) return new Set();
      return new Set(EDITIONS_PERIODIQUES_OPTIONS.map((o) => o.key));
    });
  }, []);

  const handleChildToggle = React.useCallback((key: EditionPeriodiqueType) => {
    setSelectedEditions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleEdit = React.useCallback(async () => {
    if (!previsionnel || selectedEditions.size === 0) return;
    setEditionsInFlight(true);
    try {
      const response = (await request.post(postArreteEditionsPeriodique(), {
        types: Array.from(selectedEditions),
        dateDebut: dayjs(previsionnel.dateDebutPeriode).format('YYYY-MM-DD'),
        dateFin: dateFinEffective.format('YYYY-MM-DD'),
        identifiantRegisseurFilter: null,
      })) as Job;
      setJob(response.identifiant);
    } catch (e: any) {
      toast.error(e?.message?.errors ?? 'Erreur lors du lancement des éditions.');
      setEditionsInFlight(false);
    }
  }, [previsionnel, selectedEditions, dateFinEffective]);

  const handleJobFinish = React.useCallback((finishedJob: Job) => {
    setJob(null);
    setEditionsInFlight(false);
    const { resultat, status } = finishedJob;
    if (status === -1) {
      toast.error('Échec de la génération des éditions.');
      return;
    }
    try {
      const result = JSON.parse(resultat) as { url?: string };
      if (result?.url) window.open(result.url, '_blank');
    } catch {
      toast.error("Réponse inattendue du service d'édition.");
      return;
    }
    setHasClickedEdit(true);
  }, []);

  const handleJobError = React.useCallback(() => {
    setJob(null);
    setEditionsInFlight(false);
    toast.error('Échec de la génération des éditions.');
  }, []);

  const handleConfirmArret = React.useCallback(async () => {
    setArretInFlight(true);
    try {
      // En procédure de rattrapage (Cas 1), on antidate le périodique au 31/12 N-1.
      // La date de fin choisie n'est transmise QU'EN sélection libre : en mode « depuis le
      // dernier arrêté » (et en procédure annuelle), on n'envoie pas dateFin pour que le back
      // conserve son comportement par défaut strictement identique (fin = aujourd'hui / exercice).
      await request.post(postArrete(), {
        type: 'Périodique',
        ...(periodeMode === 'libre' && dateFin && { dateFin: dateFin.format('YYYY-MM-DD') }),
        ...(isProcedureAnnuelle && { arreteAnnuel: true }),
      });
      updateRegie({ encaissementPossible: false, annulationPossible: false });
      toast.success('Arrêté périodique créé avec succès.');
      setConfirmationOpen(false);

      if (isProcedureAnnuelle) {
        // Cas 1 : on enchaîne immédiatement (et obligatoirement) sur l'arrêté annuel.
        await createArreteAnnuel(arreteAnnuelAnnee ?? dayjs().year() - 1);
        // Recalcule encaissementPossible / arreteAnnuelRequis côté serveur (débloque l'encaissement).
        await refreshRegie();
        onGoToListe();
      } else if (estDansFenetreArreteAnnuel(dayjs(), arreteConfig)) {
        // Dans la fenêtre d'arrêté annuel (configurée ou légale 15/12) : on propose d'enchaîner
        // sur l'annuel.
        setProposerAnnuel(true);
      } else {
        onGoToListe();
      }
    } catch (e: any) {
      toast.error(e?.message?.errors ?? "Erreur lors de l'arrêt de régie.");
    } finally {
      setArretInFlight(false);
    }
  }, [
    onGoToListe,
    updateRegie,
    isProcedureAnnuelle,
    createArreteAnnuel,
    arreteAnnuelAnnee,
    arreteConfig,
    refreshRegie,
    periodeMode,
    dateFin,
  ]);

  const handleProceedAnnuel = React.useCallback(async () => {
    // Fenêtre d'arrêté annuel → année courante (le back arbitre l'année réelle ; arg = libellé toast).
    await createArreteAnnuel(dayjs().year());
    await refreshRegie();
    setProposerAnnuel(false);
    onGoToListe();
  }, [createArreteAnnuel, onGoToListe, refreshRegie]);

  const handleRefuseAnnuel = React.useCallback(async () => {
    setProposerAnnuel(false);
    await refreshRegie();
    onGoToListe();
  }, [onGoToListe, refreshRegie]);

  const confirmContent = React.useMemo(() => {
    if (!previsionnel) return null;
    const dd = dayjs(previsionnel.dateDebutPeriode).format('DD/MM/YYYY');
    const df = dayjs(previsionnel.dateFinPeriode).format('DD/MM/YYYY');
    // En procédure de rattrapage, l'arrêté est antidaté à la fin d'exercice (configurable : 31/12 N-1
    // en prod, date de test en QA) — cf. arreteConfig.exerciceEndDate, et non daté du jour.
    if (isProcedureAnnuelle) {
      const dateArret = dayjs(arreteConfig?.exerciceEndDate).format('D MMMM YYYY');
      return (
        <>
          <p>Attention, cette action est irréversible !</p>
          <p>
            Vous allez arrêter votre régie en l&apos;antidatant au <strong>{dateArret}</strong> pour
            la période du <strong>{dd}</strong> au <strong>{df}</strong>. L&apos;arrêté annuel sera
            ensuite créé automatiquement.
          </p>
        </>
      );
    }
    const dateArret = dayjs().format('D MMMM YYYY');
    if (hasActivityToday) {
      return (
        <>
          <p>Attention, cette action est irréversible !</p>
          <p>
            Au moins un encaissement ou annulation ont été effectués aujourd&apos;hui. Si vous
            arrêtez maintenant votre régie, vous ne pourrez plus encaisser sur cette journée.
          </p>
          <p>
            Voulez-vous arrêter votre régie au <strong>{dateArret}</strong> pour la période du{' '}
            <strong>{dd}</strong> au <strong>{df}</strong> ?
          </p>
        </>
      );
    }
    return (
      <>
        <p>Attention, cette action est irréversible !</p>
        <p>Aucun encaissement ou annulation n&apos;ont été effectués aujourd&apos;hui.</p>
        <p>
          Voulez-vous arrêter votre régie au <strong>{dateArret}</strong> pour la période du{' '}
          <strong>{dd}</strong> au <strong>{df}</strong> ?
        </p>
      </>
    );
  }, [previsionnel, hasActivityToday, isProcedureAnnuelle, arreteConfig]);

  if (loadingPrevisionnel) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement du prévisionnel</Loader>
      </Dimmer>
    );
  }

  if (!previsionnel) return null;

  const totalEditions = EDITIONS_PERIODIQUES_OPTIONS.length;
  const parentChecked = selectedEditions.size === totalEditions;
  const parentIndeterminate = selectedEditions.size > 0 && selectedEditions.size < totalEditions;

  return (
    <Segment
      inverted
      secondary
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'transparent',
      }}
    >
      <Segment
        style={{
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header as="h1" textAlign="center" style={{ marginBottom: '1em' }}>
          <Header.Content>{`Arrêté périodique ${previsionnel.annee} - n°${previsionnel.rang}`}</Header.Content>
        </Header>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '2em',
            marginBottom: '1.5em',
            padding: '0 1em',
          }}
        >
          <div>
            <Header as="h4" style={{ marginBottom: '0.5em' }}>
              Période
            </Header>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <span>Du</span>
              <Input
                disabled
                value={dayjs(previsionnel.dateDebutPeriode).format('DD/MM/YYYY')}
                style={{ width: 140 }}
              />
              <span>au</span>
              <Input
                disabled
                value={dayjs(previsionnel.dateFinPeriode).format('DD/MM/YYYY')}
                style={{ width: 140 }}
              />
            </div>
          </div>
          <div>
            <Header as="h4" style={{ marginBottom: '0.5em' }}>
              Sélection des encaissements / annulations réalisés par
            </Header>
            <Dropdown
              selection
              disabled
              value="tous"
              options={DROPDOWN_OPTIONS}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <Segment style={{ margin: '0 1em 1em 1em', width: 'fit-content' }}>
          <div
            style={{
              paddingBottom: '0.5em',
              borderBottom: '1px solid #e0e0e0',
              marginBottom: '0.75em',
            }}
          >
            <Checkbox
              label="Sélection des éditions accompagnant l'arrêt"
              checked={parentChecked}
              indeterminate={parentIndeterminate}
              onChange={handleParentToggle}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1em',
              paddingLeft: '1.5em',
              paddingTop: '0.5em',
            }}
          >
            {EDITIONS_PERIODIQUES_OPTIONS.map((option) => (
              <Checkbox
                key={option.key}
                label={option.label}
                checked={selectedEditions.has(option.key)}
                onChange={() => handleChildToggle(option.key)}
              />
            ))}
          </div>
        </Segment>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '1em',
            paddingRight: '1em',
            gap: '1em',
          }}
        >
          <Button content="Revenir à la liste des arrêtés" onClick={onGoToListe} basic />
          <Button
            positive
            size="large"
            content="Éditer les états"
            loading={editionsInFlight}
            disabled={selectedEditions.size === 0 || editionsInFlight}
            onClick={handleEdit}
          />
          {hasClickedEdit && (
            <Button
              positive
              size="large"
              content="Arrêter la régie"
              onClick={() => setConfirmationOpen(true)}
            />
          )}
        </div>
      </Segment>

      {job !== null && (
        <TraitementLong
          title="Édition de l'arrêté périodique en cours"
          identifiantJob={job}
          onFinish={handleJobFinish}
          onError={handleJobError}
          refreshInterval={3000}
        />
      )}

      {confirmationOpen && (
        <ModalConfirm
          header="Confirmation de l'arrêt de régie"
          content={confirmContent}
          confirmText="Confirmer l'arrêt de régie"
          cancelText="Annuler"
          loading={arretInFlight}
          onConfirm={handleConfirmArret}
          onCancel={() => setConfirmationOpen(false)}
        />
      )}

      {proposerAnnuel && (
        <ModalConfirm
          header="Arrêté annuel"
          content="Voulez-vous procéder à l'arrêté annuel de votre régie ?"
          confirmText="Oui"
          cancelText="Non"
          loading={creatingAnnuel}
          onConfirm={handleProceedAnnuel}
          onCancel={handleRefuseAnnuel}
        />
      )}
    </Segment>
  );
};

export default ArretePeriodiqueEditions;
